import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  ListItemIcon,
  MenuItem,
  Select,
  Stack,
  Typography,
  styled,
} from '@mui/material'
import Link from 'next/link'
import Image from 'next/image'
import React, { useState } from 'react'
import { Email, Telegram, Twitter } from '@mui/icons-material'
import DescriptionIcon from '@mui/icons-material/Description'
import { useAccount, useNetwork, useSwitchNetwork } from 'wagmi'
import { useWeb3Modal } from '@web3modal/react'
import { hide, show } from '@ebay/nice-modal-react'
import { useWallet } from '../common/WalletContext'
import BitcoinWalletConnectDialog from '../common/BitcoinConnectDialog'
import TokenLogo from '../common/TokenLogo'
import styles from './styles'
import LogoSvg from '@/assets/imgs/logo.svg'
import WalletSvg from '@/assets/imgs/wallet.svg'
import MenuSvg from '@/assets/imgs/Mobile/menu.svg'
import { ReactComponent as RightGreenSvg } from '@/assets/imgs/Mobile/rightGreen.svg'
import MenuCloseSvg from '@/assets/imgs/Mobile/menuClose.svg'
import { ReactComponent as RightSVG } from '@/assets/imgs/Mobile/right.svg'

import { ReactComponent as MediumSvg } from '@/assets/imgs/Mobile/medium.svg'
import { CHAIN_INFO, ETHEREUM_CHAIN_ID, SUPPORTED_CHAIN } from '@/constants'
import { toSmallAddr } from '@/utils'
import bitcoinSvg from '@/assets/imgs/bitcoin.svg'

export type IHeaderMobileProps = {}
const TextLogo = styled(Typography)`
  font-weight: 400;
  font-size: 15.2727px;
  line-height: 20px;
  color: #bfff0b;
`

const StyledIconButton = styled(IconButton)`
  opacity: 0.4;

  :hover {
    opacity: 1;
  }
`

const HeaderMobile: React.FC<IHeaderMobileProps> = ({}) => {
  const [showSlider, setShowSlider] = useState<boolean>(false)
  const { setCurrentSelectedWallet, isConnected } = useWallet()
  const { address } = useAccount()
  const { chain } = useNetwork()
  const { open: openWeb3Modal, setDefaultChain } = useWeb3Modal()
  const { switchNetwork } = useSwitchNetwork()
  const connectBtcWallet = () => {
    show(BitcoinWalletConnectDialog, {
      onConnect: () => {
        hide(BitcoinWalletConnectDialog)
      },
    })
  }
  const connectEvmWallet = (chainId: number) => {
    setDefaultChain(CHAIN_INFO[chainId])

    if (isConnected) {
      switchNetwork(chainId)
    } else {
      openWeb3Modal()
    }

    setCurrentSelectedWallet('Evm')
  }
  return (
    <AppBar sx={styles.root}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Link href={'/bridge'}>
          <Stack direction={'row'} spacing={8} alignItems={'center'}>
            <Image src={LogoSvg} alt={'logo'} width={28} height={28} />
            <TextLogo variant="h1">MultiBit</TextLogo>
          </Stack>
        </Link>
        <Stack direction={'row'} spacing={12} alignItems={'center'}>
          {isConnected ? (
            <Select
              sx={{
                height: 34,
                color: '#FFFFFF',
                border: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 0,
              }}
              value={chain?.id}
              onChange={(ev) => {
                if (ev.target.value === 'btc') {
                  connectBtcWallet()
                } else {
                  connectEvmWallet(Number(ev.target.value))
                }
              }}
              renderValue={(value) => {
                return <> {toSmallAddr(address)}</>
              }}
            >
              <MenuItem value="btc">
                <ListItemIcon sx={{ minWidth: 'auto !important' }}>
                  <TokenLogo src={bitcoinSvg} alt={'bitcoin'} size={24} />
                </ListItemIcon>

                <Typography sx={{ fontWeight: 500 }} component="span">
                  Bitcoin&nbsp;
                </Typography>
              </MenuItem>

              {Object.values(SUPPORTED_CHAIN).map((chain) => (
                <MenuItem value={chain.id} key={chain.id}>
                  <ListItemIcon sx={{ minWidth: 'auto !important' }}>
                    <TokenLogo src={chain.logoSrc} alt={chain.name} size={24} />
                  </ListItemIcon>

                  <Typography sx={{ fontWeight: 500 }} component="span">
                    {chain.name}&nbsp;
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Image
              src={WalletSvg}
              alt={'wallet'}
              width={32}
              height={32}
              onClick={() => {
                connectEvmWallet(ETHEREUM_CHAIN_ID)
              }}
            />
          )}
          <Image
            src={MenuSvg}
            alt={'menu'}
            width={24}
            height={24}
            onClick={() => {
              setShowSlider(true)
            }}
          />
        </Stack>
      </Stack>
      <Drawer
        sx={{
          '& .MuiDrawer-paper': {
            height: '100vh',
            background: '#030401',
          },
        }}
        anchor="top"
        open={showSlider}
        onClose={() => {
          setShowSlider(false)
        }}
      >
        <Stack px={24} height={86} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <Stack direction={'row'} spacing={8} alignItems={'center'}>
            <Image src={LogoSvg} alt={'logo'} width={28} height={28} />
            <TextLogo variant="h1">MultiBit</TextLogo>
          </Stack>
          <Image
            src={MenuCloseSvg}
            alt={'menu'}
            width={24}
            height={24}
            onClick={() => {
              setShowSlider(false)
            }}
          />
        </Stack>
        <Box
          p={'32px 24px 25px 24px'}
          display={'flex'}
          flex={1}
          flexDirection={'column'}
          justifyContent={'space-between'}
        >
          <Stack spacing={20} direction={'column'}>
            <Stack direction={'column'} spacing={8}>
              <Link
                href="/account"
                onClick={() => {
                  setShowSlider(false)
                }}
              >
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                  <Typography fontSize={20} lineHeight={'26px'} fontWeight={500} color={'#FFFFFF'}>
                    Account
                  </Typography>
                  <RightGreenSvg />
                </Box>
              </Link>
              <Typography
                fontSize={14}
                lineHeight={'18px'}
                fontWeight={500}
                color={'#FFFFFF'}
                sx={{
                  opacity: 0.4,
                }}
              >
                View your portfolio of related assets on this chain
              </Typography>
            </Stack>

            <Box
              sx={{
                borderBottom: '1px solid rgba(57, 61, 73, 0.5)',
                pb: 30,
                display: 'flex',
                justifyContent: 'space-between',
              }}
              onClick={() => {
                window.open('https://test.multibit.exchange/')
                setShowSlider(false)
              }}
            >
              <Typography fontSize={16} lineHeight={'20px'} fontWeight={400} color={'#FCFCFD'}>
                About
              </Typography>
              <RightSVG />
            </Box>

            <Link
              href={'/bridge'}
              target={'_self'}
              onClick={() => {
                setShowSlider(false)
              }}
            >
              <Box
                sx={{
                  borderBottom: '1px solid rgba(57, 61, 73, 0.5)',
                  pb: 30,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Typography color={'#FCFCFD'} fontSize={16} lineHeight={'20px'} fontWeight={400}>
                  Bridge
                </Typography>
                <RightSVG />
              </Box>
            </Link>

            <Link
              href={'/staking'}
              onClick={() => {
                setShowSlider(false)
              }}
            >
              <Box
                sx={{
                  borderBottom: '1px solid rgba(57, 61, 73, 0.5)',
                  pb: 30,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Typography color={'#FCFCFD'} fontSize={16} lineHeight={'20px'} fontWeight={400}>
                  🔥 Staking
                </Typography>
                <RightSVG />
              </Box>
            </Link>
            <Box
              sx={{
                borderBottom: '1px solid rgba(57, 61, 73, 0.5)',
                pb: 30,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography color={'#FCFCFD'} fontSize={16} lineHeight={'20px'} fontWeight={400}>
                Farm <span style={{ opacity: 0.4 }}>(SOON)</span>
              </Typography>
              <RightSVG />
            </Box>
            <Box
              sx={{
                borderBottom: '1px solid rgba(57, 61, 73, 0.5)',
                pb: 30,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography color={'#FCFCFD'} fontSize={16} lineHeight={'20px'} fontWeight={400}>
                Swap <span style={{ opacity: 0.4 }}>(SOON)</span>
              </Typography>
              <RightSVG />
            </Box>
          </Stack>
          <Stack direction={'row'} spacing={12} alignContent={'center'}>
            <Link href="https://docs.multibit.exchange/multibit" target="_blank">
              <StyledIconButton>
                <DescriptionIcon />
              </StyledIconButton>
            </Link>
            <Link href="mailto:info@multibit.exchange" target="_blank">
              <StyledIconButton>
                <Email />
              </StyledIconButton>
            </Link>
            <Link href="https://t.me/multibitprotocol" target="_blank">
              <StyledIconButton>
                <Telegram />
              </StyledIconButton>
            </Link>
            <Link href="https://twitter.com/Multibit_Bridge" target="_blank">
              <StyledIconButton>
                <Twitter />
              </StyledIconButton>
            </Link>
            <Link href="https://medium.com/@Multibit_Bridge" target="_blank">
              <StyledIconButton>
                <MediumSvg />
              </StyledIconButton>
            </Link>
          </Stack>
        </Box>
      </Drawer>
    </AppBar>
  )
}

export default HeaderMobile
